import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { FileUp, Loader2, CheckCircle2, Pencil, Plus, Copy, Sparkles, BookOpenText, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const TYPE_LABEL = { asset: "Aktiv", liability: "Passiv", equity: "Eigenkapital", income: "Ertrag", expense: "Aufwand" };
const STATUS = {
  missing: { label: "Fehlt – wird angelegt", cls: "bg-rose-100 text-rose-800", Icon: Plus },
  rename: { label: "Andere Bezeichnung", cls: "bg-amber-100 text-amber-800", Icon: Pencil },
  same: { label: "Vorhanden", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
};
const chf = (v) => (v == null ? "" : Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const inputCls = "h-8 w-full rounded border border-slate-300 bg-white px-2 text-xs";

export function SettingsChartImportSection({ companyId, isViewer, onApplied, undescribedCount = 0, setupDone = false }) {
  const navigate = useNavigate();
  const onboarding = !setupDone && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("onboarding") === "chart";
  const [describeAi, setDescribeAi] = useState(true);
  const [describing, setDescribing] = useState(false);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showSame, setShowSame] = useState(false);
  const [mode, setMode] = useState(null); // null | copy | ai
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [desc, setDesc] = useState("");
  const [income, setIncome] = useState("");

  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/accounts/copy-sources`).then((r) => setSources(r.data || [])).catch(() => setSources([]));
  }, [companyId]);

  const showPreview = (data, msg) => {
    setPreview(data);
    setSelected(new Set(data.items.filter((i) => i.status !== "same").map((i) => i.code)));
    setMode(null);
    toast.success(msg || `${data.items.length} Konten · ${data.summary.missing} fehlen · ${data.summary.rename} anders benannt`);
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      [...files].forEach((f) => fd.append("files", f));
      const r = await apiClient.post(`/companies/${companyId}/accounts/import-preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      showPreview(r.data, `${r.data.items.length} Konten gelesen · ${r.data.summary.missing} fehlen · ${r.data.summary.rename} anders benannt`);
    } catch (e) { toast.error(e.response?.data?.detail || "Datei konnte nicht gelesen werden."); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const suggest = async (body) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/accounts/suggest`, body);
      showPreview(r.data, body.source_company_id ? "Kontenplan geladen – Bezeichnungen können Sie vor der Übernahme anpassen." : "KI-Vorschlag erstellt – bitte prüfen und anpassen.");
    } catch (e) { toast.error(e.response?.data?.detail || "Vorschlag fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    const items = preview.items.filter((i) => selected.has(i.code) && i.status !== "same").map((i) => ({ code: i.code, name: i.name, type: i.type }));
    if (!items.length) return;
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/accounts/import-apply`, { items, rename_existing: true, describe: describeAi });
      toast.success(`${r.data.created_count} Konten angelegt · ${r.data.renamed_count} umbenannt${r.data.describing ? " · Beschreibungen werden im Hintergrund ergänzt" : ""}`);
      if (r.data.describing) setTimeout(() => onApplied?.(), 30000);
      setPreview(null);
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  const describeNow = async () => {
    setDescribing(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/accounts/describe`, { only_empty: true });
      toast.success(`${r.data.updated} Konto-Beschreibungen ergänzt.`);
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Beschreibungen konnten nicht ergänzt werden."); }
    finally { setDescribing(false); }
  };

  const toggle = (code) => setSelected((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const editName = (code, name) => setPreview((p) => ({ ...p, items: p.items.map((i) => (i.code === code ? { ...i, name, status: i.existing_name ? ((i.existing_name || "").trim().toLowerCase() === name.trim().toLowerCase() ? "same" : "rename") : "missing" } : i)) }));
  const rows = preview ? preview.items.filter((i) => showSame || i.status !== "same") : [];
  const pending = preview ? preview.items.filter((i) => selected.has(i.code) && i.status !== "same").length : 0;

  return (
    <div className="mb-4 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/40 p-3" data-testid="chart-import-section">
      {onboarding && !preview && (
        <div className="mb-3 rounded-lg border border-indigo-300 bg-white p-4" data-testid="chart-onboarding">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Schritt 1 von 2 · Neue Firma</div>
          <h3 className="text-base font-semibold text-slate-900 mt-0.5">Kontenplan einrichten</h3>
          <p className="text-xs text-slate-600 mt-1">Ein Schweizer KMU-Standardkontenplan ist bereits angelegt. Passen Sie ihn jetzt mit einem Klick an Ihre Firma an:</p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              ["upload", FileUp, "Bilanz / ER vom Treuhänder", "PDF oder CSV hochladen – Konten und Bezeichnungen werden übernommen", () => inputRef.current?.click(), true],
              ["copy", Copy, "Von anderer Firma übernehmen", "Kontenplan einer bestehenden Firma kopieren und nur einzelne Namen ändern", () => setMode("copy"), sources.length > 0],
              ["ai", Sparkles, "KI-Vorschlag", "Kurz beschreiben, was die Firma macht – die KI schlägt passende Konten vor", () => setMode("ai"), true],
            ].filter((o) => o[5]).map(([key, Icon, title, desc, onClick]) => (
              <button key={key} type="button" onClick={onClick} disabled={busy} className="text-left rounded-lg border border-slate-200 p-3 hover:border-indigo-400 hover:bg-indigo-50 transition-colors" data-testid={`chart-onboarding-${key}`}>
                <Icon className="h-5 w-5 text-indigo-700" />
                <div className="mt-1 text-sm font-medium text-slate-900">{title}</div>
                <div className="text-[11px] text-slate-500">{desc}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
            <button type="button" onClick={() => navigate("/")} className="text-slate-500 underline-offset-2 hover:underline" data-testid="chart-onboarding-skip">Standard-Kontenplan behalten und später anpassen</button>
            <span className="ml-auto text-slate-500 inline-flex items-center gap-1">Schritt 2: Saldi der alten Buchhaltung übernehmen <ArrowRight className="h-3 w-3" /> <a href="/opening-balance?onboarding=chart" className="text-indigo-700 underline-offset-2 hover:underline" data-testid="chart-onboarding-opening">Eröffnungsbilanz</a></span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <FileUp className="h-4 w-4 text-indigo-700" />
        <div className="flex-1 min-w-[16rem]">
          <div className="text-sm font-medium text-slate-800">Kontenplan einrichten / abgleichen</div>
          <div className="text-xs text-slate-500">Bilanz/ER-PDF des Treuhänders hochladen, Kontenplan einer anderen Firma übernehmen oder von der KI vorschlagen lassen – fehlende Konten werden angelegt, Bezeichnungen übernommen. Buchungen bleiben unberührt.</div>
        </div>
        <input ref={inputRef} type="file" multiple accept=".pdf,.csv,.xlsx" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="chart-import-file-input" />
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy || isViewer} data-testid="chart-import-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileUp className="h-4 w-4 mr-1" />}Bilanz / ER hochladen
        </Button>
        {sources.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setMode(mode === "copy" ? null : "copy")} disabled={busy || isViewer} data-testid="chart-copy-btn">
            <Copy className="h-4 w-4 mr-1" />Von anderer Firma übernehmen
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => setMode(mode === "ai" ? null : "ai")} disabled={busy || isViewer} data-testid="chart-ai-btn">
          <Sparkles className="h-4 w-4 mr-1" />KI-Vorschlag
        </Button>
        {undescribedCount > 0 && (
          <Button size="sm" variant="outline" onClick={describeNow} disabled={describing || isViewer} title="Für Konten ohne Beschreibung schreibt die KI, was darauf gebucht wird – verbessert die Buchungsvorschläge" data-testid="chart-describe-btn">
            {describing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <BookOpenText className="h-4 w-4 mr-1" />}{undescribedCount} Beschreibungen ergänzen (KI)
          </Button>
        )}
      </div>

      {mode === "copy" && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded border border-slate-200 bg-white p-3" data-testid="chart-copy-panel">
          <div className="flex-1 min-w-[14rem]">
            <label className="text-xs text-slate-600">Kontenplan übernehmen von</label>
            <select className={inputCls} value={sourceId} onChange={(e) => setSourceId(e.target.value)} data-testid="chart-copy-source">
              <option value="">Firma wählen …</option>
              {sources.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.accounts} Konten)</option>)}
            </select>
          </div>
          <Button size="sm" onClick={() => suggest({ source_company_id: sourceId })} disabled={!sourceId || busy} data-testid="chart-copy-load">Vorschau laden</Button>
          <span className="w-full text-[11px] text-slate-500">Danach können Sie einzelne Bezeichnungen anpassen, z.B. «Fitnessabonnemente» → «Einnahmen Reinigung».</span>
        </div>
      )}

      {mode === "ai" && (
        <div className="mt-3 space-y-2 rounded border border-slate-200 bg-white p-3" data-testid="chart-ai-panel">
          <div>
            <label className="text-xs text-slate-600">Was macht die Firma? *</label>
            <textarea className="w-full rounded border border-slate-300 px-2 py-1 text-xs" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="z.B. Reinigungsfirma mit 3 Angestellten, Büro- und Unterhaltsreinigung für Firmenkunden, ein Firmenfahrzeug" data-testid="chart-ai-desc" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Welche Einnahmen gibt es?</label>
            <input className={inputCls} value={income} onChange={(e) => setIncome(e.target.value)} placeholder="z.B. Unterhaltsreinigung, Fensterreinigung, Materialverkauf" data-testid="chart-ai-income" />
          </div>
          <Button size="sm" onClick={() => suggest({ description: desc, income_types: income })} disabled={!desc.trim() || busy} data-testid="chart-ai-run">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}Kontenplan vorschlagen
          </Button>
        </div>
      )}

      {preview && (
        <div className="mt-3 space-y-2" data-testid="chart-import-preview">
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <span data-testid="chart-import-summary">{preview.summary.missing} fehlen · {preview.summary.rename} anders benannt · {preview.summary.same} identisch · {preview.summary.only_app} nur in der App</span>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={showSame} onChange={(e) => setShowSame(e.target.checked)} data-testid="chart-import-show-same" /> identische anzeigen</label>
            <span className="ml-auto text-slate-400">{preview.sources.map((s) => `${s.file} (${s.count})`).join(" · ")}</span>
          </div>
          <div className="max-h-96 overflow-auto rounded border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="w-8"></th><th className="text-left px-2 py-1">Konto</th><th className="text-left w-72">Bezeichnung (anpassbar)</th><th className="text-left">Bisher in der App</th><th className="text-left">Typ</th><th className="text-right pr-2">Saldo</th><th className="text-left">Status</th></tr></thead>
              <tbody>
                {rows.map((i) => { const st = STATUS[i.status]; return (
                  <tr key={i.code} className={`border-t border-slate-100 ${i.status === "same" ? "text-slate-400" : ""}`} data-testid={`chart-import-row-${i.code}`}>
                    <td className="text-center">{i.status !== "same" && <input type="checkbox" checked={selected.has(i.code)} onChange={() => toggle(i.code)} data-testid={`chart-import-select-${i.code}`} />}</td>
                    <td className="px-2 py-1 font-mono">{i.code}</td>
                    <td className="py-1 pr-2">
                      <input className={inputCls} value={i.name} onChange={(e) => editName(i.code, e.target.value)} data-testid={`chart-import-name-${i.code}`} />
                      {i.hint && <div className="text-[10px] text-slate-400 mt-0.5">{i.hint}</div>}
                    </td>
                    <td className="text-slate-500">{i.existing_name || <span className="italic text-rose-600">– nicht vorhanden –</span>}</td>
                    <td>{TYPE_LABEL[i.type] || i.type}</td>
                    <td className="text-right pr-2 tabular-nums">{chf(i.amount)}</td>
                    <td><span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${st.cls}`}><st.Icon className="h-3 w-3" />{st.label}</span></td>
                  </tr>); })}
                {rows.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-500">Alle Konten sind bereits identisch vorhanden.</td></tr>}
              </tbody>
            </table>
          </div>
          {preview.only_app.length > 0 && <div className="text-[11px] text-slate-500">Nur in der App (bleiben unverändert): {preview.only_app.join(", ")}</div>}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={apply} disabled={busy || isViewer || !pending} data-testid="chart-import-apply-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}{pending} Konten übernehmen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)} data-testid="chart-import-cancel-btn">Abbrechen</Button>
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600"><input type="checkbox" checked={describeAi} onChange={(e) => setDescribeAi(e.target.checked)} data-testid="chart-import-describe" /> Beschreibungen per KI ergänzen (bessere Buchungsvorschläge)</label>
            <span className="text-[11px] text-slate-500">Tipp: Für den Jahresabschluss 2025 die Saldi unter «Abschluss & Analyse → Eröffnungsbilanz» aus demselben PDF übernehmen.</span>
          </div>
        </div>
      )}
    </div>
  );
}
